import * as THREE from "three";
import type { AvatarConfig } from "@shared/types";
import { LANE_POSITIONS, RUNNER } from "@shared/constants";
import type { Path } from "./Path";
import type { MovementMode } from "../../local/gameSettings";

type State = "run" | "jump" | "slide" | "hit";

const _tmpVec = new THREE.Vector3();
const FREE_HALF_WIDTH = 18;
const FREE_STEER_ACCEL = 34;
const FREE_STEER_DAMPING = 10;
const MAX_LATERAL_SPEED = 13;
const PLAYER_SCALE = 0.25;

function buildHat(id: string): THREE.Object3D {
  const g = new THREE.Group();
  switch (id) {
    case "cap-blue":
    case "cap-red": {
      const color = id === "cap-blue" ? 0x2563eb : 0xdc2626;
      const crown = new THREE.Mesh(
        new THREE.BoxGeometry(0.78, 0.32, 0.78),
        new THREE.MeshStandardMaterial({ color }),
      );
      crown.position.set(0, 2.92, 0);
      crown.castShadow = true;
      g.add(crown);
      const brim = new THREE.Mesh(
        new THREE.BoxGeometry(0.78, 0.08, 0.45),
        new THREE.MeshStandardMaterial({ color }),
      );
      brim.position.set(0, 2.78, 0.5);
      brim.castShadow = true;
      g.add(brim);
      break;
    }
    case "beanie": {
      const beanie = new THREE.Mesh(
        new THREE.BoxGeometry(0.82, 0.4, 0.82),
        new THREE.MeshStandardMaterial({ color: 0xef476f }),
      );
      beanie.position.set(0, 2.96, 0);
      beanie.castShadow = true;
      g.add(beanie);
      const pom = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 8, 8),
        new THREE.MeshStandardMaterial({ color: 0xffffff }),
      );
      pom.position.set(0, 3.22, 0);
      pom.castShadow = true;
      g.add(pom);
      break;
    }
    case "crown": {
      const band = new THREE.Mesh(
        new THREE.BoxGeometry(0.78, 0.18, 0.78),
        new THREE.MeshStandardMaterial({ color: 0xffd166, metalness: 0.6, roughness: 0.3 }),
      );
      band.position.set(0, 2.85, 0);
      band.castShadow = true;
      g.add(band);
      // Spikes
      for (let i = -1; i <= 1; i++) {
        for (let j = -1; j <= 1; j++) {
          if (Math.abs(i) + Math.abs(j) !== 1) continue;
          const spike = new THREE.Mesh(
            new THREE.ConeGeometry(0.1, 0.3, 4),
            new THREE.MeshStandardMaterial({ color: 0xffd166, metalness: 0.6, roughness: 0.3 }),
          );
          spike.position.set(i * 0.3, 3.05, j * 0.3);
          g.add(spike);
        }
      }
      break;
    }
  }
  return g;
}

function angleDelta(current: number, target: number) {
  return THREE.MathUtils.euclideanModulo(target - current + Math.PI, Math.PI * 2) - Math.PI;
}

function dampAngle(current: number, target: number, alpha: number) {
  return current + angleDelta(current, target) * THREE.MathUtils.clamp(alpha, 0, 1);
}

function smoothstep(t: number) {
  t = THREE.MathUtils.clamp(t, 0, 1);
  return t * t * (3 - 2 * t);
}

function powerPulse(now: number, start: number, end: number) {
  if (now >= end) return 0;
  const t = (now - start) / Math.max(1, end - start);
  return Math.sin(THREE.MathUtils.clamp(t, 0, 1) * Math.PI);
}

function capsulePart(
  radius: number,
  length: number,
  color: THREE.ColorRepresentation,
  roughness = 0.58,
) {
  const mesh = new THREE.Mesh(
    new THREE.CapsuleGeometry(radius, length, 16, 24),
    new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.03 }),
  );
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

export class Player {
  group = new THREE.Group();
  state: State = "run";
  lane = 2;
  laneTarget = 2;
  vy = 0;
  y = 0;
  jumpsLeft = 1;
  doubleJumpUnlocked = false;
  slideUntil = 0;
  hitUntil = 0;

  private parts: { ll: THREE.Object3D; rl: THREE.Object3D };
  private body: THREE.Group;
  private runT = 0;
  /** Smooth lateral lane offset (m), interpolated toward LANE_POSITIONS[laneTarget]. */
  laneOffset = 0;
  /** Lane-shift critically-damped spring velocity. */
  private laneVel = 0;
  /** Optional path the player runs along; if set, group position/rotation come from it. */
  path: Path | null = null;
  /** Arc length progress along the path. */
  s = 0;
  /** Constant lateral offset for MP slot lineups (added to laneOffset). */
  slotOffset = 0;
  movementMode: MovementMode = "free";
  // Crumble (death) state.
  private crumbling = false;
  private crumblePieces: { mesh: THREE.Object3D; vel: THREE.Vector3; spin: THREE.Vector3 }[] = [];
  private boostUntil = 0;
  private boostStartedAt = 0;
  private tumbleUntil = 0;
  private tumbleSpin = 0;
  private moveIntensity = 0;
  private lastJumpAt = 0;
  private fallPose = false;
  private groundedOnSurface = false;
  private desiredWorldYaw: number | null = null;
  private desiredMoveAmount = 0;
  private facingAlignment = 1;

  constructor(avatar: AvatarConfig) {
    const suit = new THREE.Color(avatar.shirtColor ?? "#ff2ca4");
    const faceColor = new THREE.Color(avatar.skinColor ?? "#ffd6ef").lerp(new THREE.Color("#ffffff"), 0.34);
    const footColor = new THREE.Color(avatar.pantsColor ?? avatar.shirtColor ?? "#e11d8a")
      .lerp(new THREE.Color("#111827"), 0.12);

    this.body = new THREE.Group();
    this.group.add(this.body);
    this.body.scale.setScalar(PLAYER_SCALE);

    const torso = new THREE.Mesh(
      new THREE.SphereGeometry(0.92, 32, 32),
      new THREE.MeshStandardMaterial({ color: suit, roughness: 0.55, metalness: 0.03 }),
    );
    torso.scale.set(0.9, 1.34, 0.68);
    torso.position.y = 1.62;
    torso.castShadow = true;
    torso.receiveShadow = true;
    this.body.add(torso);

    const face = new THREE.Mesh(
      new THREE.SphereGeometry(0.38, 24, 20),
      new THREE.MeshStandardMaterial({ color: faceColor, roughness: 0.5 }),
    );
    face.scale.set(1.05, 1.18, 0.15);
    face.position.set(0, 2.05, 0.63);
    this.body.add(face);

    const eyeMat = new THREE.MeshBasicMaterial({ color: 0x111111 });
    for (const dx of [-0.15, 0.15]) {
      const eye = new THREE.Mesh(new THREE.CapsuleGeometry(0.035, 0.12, 6, 12), eyeMat);
      eye.position.set(dx, 2.07, 0.7);
      this.body.add(eye);
    }

    // Hat
    const hatId = avatar.hatId ?? "none";
    if (hatId !== "none") {
      const hat = buildHat(hatId);
      this.body.add(hat);
    }

    const leg = (x: number) => {
      const pivot = new THREE.Group();
      pivot.position.set(x, 0.58, 0.1);
      const m = capsulePart(0.18, 0.5, suit);
      m.position.y = -0.3;
      const foot = new THREE.Mesh(
        new THREE.SphereGeometry(0.22, 18, 14),
        new THREE.MeshStandardMaterial({ color: footColor, roughness: 0.62, metalness: 0.02 }),
      );
      foot.scale.set(0.95, 0.62, 1.35);
      foot.position.set(0, -0.64, 0.15);
      foot.castShadow = true;
      pivot.add(m);
      pivot.add(foot);
      this.body.add(pivot);
      return pivot;
    };
    const ll = leg(-0.28);
    const rl = leg(0.28);

    this.parts = { ll, rl };

    this.group.position.x = LANE_POSITIONS[this.lane]!;
    this.group.position.z = 0;
    this.laneOffset = LANE_POSITIONS[this.lane]!;
  }

  moveLane(delta: number) {
    if (this.state === "hit") return;
    if (this.movementMode === "free") {
      this.laneVel += delta * 4.5;
      return;
    }
    this.laneTarget = Math.max(0, Math.min(LANE_POSITIONS.length - 1, this.laneTarget + delta));
  }

  /** Dodge: skip TWO lanes in the given direction (double-tap). */
  dodge(delta: number) {
    if (this.state === "hit") return;
    if (this.movementMode === "free") {
      this.laneVel += delta * 9;
      return;
    }
    this.laneTarget = Math.max(
      0,
      Math.min(LANE_POSITIONS.length - 1, this.laneTarget + delta * 2),
    );
  }

  steer(axis: number, dt: number) {
    if (this.movementMode !== "free" || this.state === "hit") return;
    const targetVel = THREE.MathUtils.clamp(axis, -1, 1) * 10.2;
    const follow = 1 - Math.exp(-12 * dt);
    this.laneVel += (targetVel - this.laneVel) * follow;
  }

  bumpSideways(direction: number) {
    if (this.movementMode !== "free" || this.state === "hit") return;
    this.applyImpulse(Math.max(-1, Math.min(1, direction)) * 7, 0);
  }

  applyImpulse(lateral: number, vertical = 0) {
    if (this.movementMode === "free") {
      this.laneVel = THREE.MathUtils.clamp(this.laneVel + lateral, -MAX_LATERAL_SPEED, MAX_LATERAL_SPEED);
    } else if (Math.abs(lateral) > 0.1) {
      this.moveLane(lateral > 0 ? 1 : -1);
    }
    if (vertical > 0) {
      this.vy = Math.max(this.vy, vertical);
      this.y = Math.max(0.05, this.y);
      if (this.state !== "slide") this.state = "jump";
    }
  }

  setMoveIntensity(value: number) {
    this.moveIntensity = THREE.MathUtils.clamp(value, 0, 1);
  }

  setDesiredFacing(yaw: number | null, moveAmount = 0) {
    this.desiredWorldYaw = yaw;
    this.desiredMoveAmount = THREE.MathUtils.clamp(moveAmount, 0, 1);
    if (yaw == null || this.desiredMoveAmount < 0.05) {
      this.facingAlignment = 1;
      return;
    }
    const angle = Math.abs(angleDelta(this.group.rotation.y, yaw));
    const rawAlignment = THREE.MathUtils.clamp(1 - angle / 1.35, 0, 1);
    this.facingAlignment = smoothstep(rawAlignment);
  }

  getMovementAlignment() {
    return this.facingAlignment;
  }

  getLateralVelocity() {
    return this.laneVel;
  }

  getRadius() {
    return 0.34;
  }

  resetOnCourse(path: Path, s: number, laneOffset = 0) {
    this.path = path;
    this.s = s;
    this.laneOffset = laneOffset;
    this.laneTarget = 2;
    this.lane = 2;
    this.laneVel = 0;
    this.y = 0;
    this.vy = 0;
    this.jumpsLeft = 1;
    this.groundedOnSurface = false;
    this.state = "run";
    this.fallPose = false;
    this.group.rotation.x = 0;
    this.group.rotation.z = 0;
    this.body.visible = true;
  }

  setFallPose(enabled: boolean) {
    this.fallPose = enabled;
    if (enabled) {
      this.state = "hit";
      this.hitUntil = Number.POSITIVE_INFINITY;
      this.tumbleUntil = Number.POSITIVE_INFINITY;
      this.tumbleSpin = this.laneVel >= 0 ? -1 : 1;
    } else if (this.state === "hit" && !Number.isFinite(this.hitUntil)) {
      this.state = "run";
      this.hitUntil = 0;
      this.tumbleUntil = 0;
    }
  }

  knockdown(now: number, lateral: number, vertical = 0.45) {
    this.applyImpulse(lateral, vertical);
    this.state = "hit";
    const hitStrength = Math.hypot(lateral, vertical);
    const recoveryMs = THREE.MathUtils.clamp(820 + hitStrength * 70, 980, 1850);
    this.hitUntil = now + recoveryMs;
    this.tumbleUntil = now + recoveryMs * 0.82;
    this.tumbleSpin = lateral >= 0 ? -1 : 1;
  }

  overlapsLane(lane: number, radius = 0.75): boolean {
    return Math.abs(this.laneOffset - LANE_POSITIONS[lane]!) <= radius;
  }

  jump(now: number) {
    if (this.state === "hit") return;
    if (this.state === "slide") return;
    if (this.y > 0.08 && !this.groundedOnSurface) return;
    if (this.jumpsLeft <= 0) return;
    this.vy = RUNNER.jumpVelocity * 0.78;
    this.jumpsLeft -= 1;
    this.state = "jump";
    this.groundedOnSurface = false;
    this.lastJumpAt = now;
  }

  landOn(relativeHeight: number) {
    if (relativeHeight < -0.05) return;
    this.y = relativeHeight;
    this.vy = 0;
    this.jumpsLeft = 1;
    this.groundedOnSurface = true;
    if (this.state === "jump") this.state = "run";
  }

  clearSurfaceSupport() {
    this.groundedOnSurface = false;
  }

  slide(now: number) {
    if (this.state !== "run") return;
    this.state = "slide";
    this.slideUntil = now + RUNNER.slideDurationMs;
  }

  hit(now: number) {
    this.knockdown(now, this.laneVel >= 0 ? -5 : 5, 3.5);
  }

  boost(now: number) {
    this.boostStartedAt = now;
    this.boostUntil = now + 1000;
  }

  /** Detach all body parts and give them random velocity/spin. */
  crumble() {
    if (this.crumbling) return;
    this.crumbling = true;
    const collect: THREE.Object3D[] = [];
    this.body.traverse((o) => {
      if (o instanceof THREE.Mesh) collect.push(o);
    });
    for (const m of collect) {
      // Re-parent to player group (not body) so we can drive each piece freely.
      const worldPos = new THREE.Vector3();
      m.getWorldPosition(worldPos);
      this.group.attach(m);
      m.position.copy(this.group.worldToLocal(worldPos));
      const v = new THREE.Vector3(
        (Math.random() - 0.5) * 4,
        2 + Math.random() * 4,
        (Math.random() - 0.5) * 2,
      );
      const spin = new THREE.Vector3(
        (Math.random() - 0.5) * 8,
        (Math.random() - 0.5) * 8,
        (Math.random() - 0.5) * 8,
      );
      this.crumblePieces.push({ mesh: m, vel: v, spin });
    }
    this.body.visible = false;
  }

  /** AABB used for collision against obstacles. */
  bounds(): THREE.Box3 {
    const x = this.group.position.x;
    const z = this.group.position.z;
    const y = this.y;
    let h = 2.8;
    let yOff = 1.4;
    if (this.state === "slide") {
      h = 1.0;
      yOff = 0.5;
    }
    return new THREE.Box3(
      new THREE.Vector3(x - 0.45, y + yOff - h / 2, z - 0.4),
      new THREE.Vector3(x + 0.45, y + yOff + h / 2, z + 0.4),
    );
  }

  update(dt: number, now: number) {
    if (this.crumbling) {
      // Free-flying voxel pieces with gravity.
      for (const p of this.crumblePieces) {
        p.vel.y -= 12 * dt;
        p.mesh.position.addScaledVector(p.vel, dt);
        p.mesh.rotation.x += p.spin.x * dt;
        p.mesh.rotation.y += p.spin.y * dt;
        p.mesh.rotation.z += p.spin.z * dt;
        // Bounce off ground (y=0 in player-local space below player center)
        if (p.mesh.position.y < -1.4) {
          p.mesh.position.y = -1.4;
          p.vel.y *= -0.35;
          p.vel.x *= 0.85;
          p.vel.z *= 0.85;
        }
      }
      return;
    }
    if (this.movementMode === "free") {
      const airborne = this.state === "jump" || this.y > 0.08;
      const dampingRate = this.state === "hit" ? 2.0 : airborne ? 1.2 : FREE_STEER_DAMPING;
      const damping = Math.exp(-dampingRate * dt);
      this.laneVel *= damping;
      this.laneVel = THREE.MathUtils.clamp(this.laneVel, -MAX_LATERAL_SPEED, MAX_LATERAL_SPEED);
      this.laneOffset += this.laneVel * dt;
      this.laneOffset = THREE.MathUtils.clamp(this.laneOffset, -FREE_HALF_WIDTH, FREE_HALF_WIDTH);
      let nearest = 0;
      let best = Infinity;
      for (let i = 0; i < LANE_POSITIONS.length; i++) {
        const d = Math.abs(this.laneOffset - LANE_POSITIONS[i]!);
        if (d < best) {
          best = d;
          nearest = i;
        }
      }
      this.laneTarget = nearest;
    } else {
      // Lane shift — critically-damped spring on the lane offset (interpreted in
      // the local "across-the-path" axis when running along a curved Path).
      const targetOffset = LANE_POSITIONS[this.laneTarget]!;
      const k = 60;
      const d = 2 * Math.sqrt(k);
      const ax = -k * (this.laneOffset - targetOffset) - d * this.laneVel;
      this.laneVel += ax * dt;
      this.laneOffset += this.laneVel * dt;
    }
    // If we're not on a path, write straight to world x. Otherwise the Game
    // (or path follower below) places us along the curve every frame.
    if (!this.path) this.group.position.x = this.laneOffset;
    else {
      const pos = this.path.positionAt(this.s, this.laneOffset + this.slotOffset, _tmpVec);
      this.group.position.x = pos.x;
      this.group.position.y = pos.y;
      this.group.position.z = pos.z;
      const targetYaw = this.desiredWorldYaw ?? this.group.rotation.y;
      const turnRate = this.desiredWorldYaw == null ? 9 : 11.5;
      this.group.rotation.y = dampAngle(this.group.rotation.y, targetYaw, 1 - Math.exp(-turnRate * dt));
      const angle = Math.abs(angleDelta(this.group.rotation.y, targetYaw));
      const rawAlignment = this.desiredWorldYaw == null ? 1 : THREE.MathUtils.clamp(1 - angle / 1.35, 0, 1);
      this.facingAlignment = this.desiredMoveAmount < 0.05 ? 1 : smoothstep(rawAlignment);
    }

    // Slide expiry
    if (this.state === "slide" && now >= this.slideUntil) {
      this.state = "run";
    }
    // Hit expiry
    if (this.state === "hit" && now >= this.hitUntil) {
      this.state = "run";
    }

    // Vertical physics
    if (this.state === "jump" || this.y > 0 || this.vy !== 0) {
      this.vy -= RUNNER.gravity * dt;
      this.y += this.vy * dt;
      if (this.y <= 0) {
        this.y = 0;
        this.vy = 0;
        this.jumpsLeft = 1;
        this.groundedOnSurface = false;
        if (this.state === "jump") this.state = "run";
      }
    }

    // Animation
    const boosting = now < this.boostUntil;
    const boostPower = powerPulse(now, this.boostStartedAt, this.boostUntil);
    const moveIntensity = this.state === "run" ? this.moveIntensity : 1;
    this.runT += dt * (10 + boostPower * 6) * Math.max(0.08, moveIntensity);
    if (this.state === "run") {
      const step = this.runT;
      const sway = Math.sin(step);
      const sideStep = Math.sin(step * 2);
      const footLiftL = Math.max(0, Math.sin(step));
      const footLiftR = Math.max(0, -Math.sin(step));
      const plantL = Math.max(0, -Math.sin(step));
      const plantR = Math.max(0, Math.sin(step));
      const legSwing = sway * (0.42 + boostPower * 0.18) * moveIntensity;

      // Correct-answer power-up: grounded lean and quicker feet, without a jump.
      this.parts.ll.rotation.x = -legSwing - footLiftL * 0.18;
      this.parts.rl.rotation.x = legSwing - footLiftR * 0.18;
      this.parts.ll.rotation.z = -0.08 - footLiftL * 0.16 * moveIntensity;
      this.parts.rl.rotation.z = 0.08 + footLiftR * 0.16 * moveIntensity;
      this.parts.ll.position.y = 0.58 + footLiftL * 0.18 * moveIntensity - plantL * 0.035 * moveIntensity;
      this.parts.rl.position.y = 0.58 + footLiftR * 0.18 * moveIntensity - plantR * 0.035 * moveIntensity;
      this.parts.ll.position.z = 0.1 + footLiftL * 0.1 * moveIntensity;
      this.parts.rl.position.z = 0.1 + footLiftR * 0.1 * moveIntensity;
      this.body.rotation.x = -0.16 * boostPower + Math.abs(sway) * 0.035 * moveIntensity;
      const turn = THREE.MathUtils.clamp(-this.laneVel * 0.035, -0.58, 0.58);
      this.body.rotation.y = turn + sideStep * 0.055 * moveIntensity;
      this.body.rotation.z = sway * (0.14 - boostPower * 0.05) * moveIntensity;
      const beanSquash = Math.abs(Math.sin(this.runT * 2)) * 0.025 * moveIntensity;
      const breath = Math.sin(now * 0.0032) * 0.018 * (1 - Math.min(1, moveIntensity * 0.85));
      const powerSquash = boostPower * 0.04;
      this.body.scale.set(
        PLAYER_SCALE * (1 + beanSquash * 0.45 - breath * 0.35 + powerSquash),
        PLAYER_SCALE * (1 - beanSquash + breath - powerSquash * 0.65),
        PLAYER_SCALE * (1 + beanSquash * 0.45 - breath * 0.35 + powerSquash),
      );
    } else if (this.state === "jump") {
      // Tiny feet tuck back/up for a simple bean jump.
      this.parts.ll.rotation.x = -1.2;
      this.parts.rl.rotation.x = -1.2;
      this.parts.ll.rotation.z = -0.05;
      this.parts.rl.rotation.z = 0.05;
      this.parts.ll.position.set(-0.28, 0.68, 0.16);
      this.parts.rl.position.set(0.28, 0.68, 0.16);
      this.body.rotation.x = -0.15;
      this.body.rotation.y = THREE.MathUtils.clamp(-this.laneVel * 0.025, -0.35, 0.35);
      this.body.scale.set(PLAYER_SCALE * 0.96, PLAYER_SCALE * 1.08, PLAYER_SCALE * 0.96);
    } else if (this.state === "slide") {
      // Superman-ish pose: torso pitches forward but stays above the road.
      // The previous -1.4 rad pitch + body.position.y=-1.1 drop sank the
      // player below the ground. -1.0 rad keeps head/torso at y > 0.
      this.parts.ll.rotation.x = -0.3;
      this.parts.rl.rotation.x = -0.3;
      this.parts.ll.rotation.z = 0;
      this.parts.rl.rotation.z = 0;
      this.parts.ll.position.set(-0.28, 0.52, 0.1);
      this.parts.rl.position.set(0.28, 0.52, 0.1);
      this.body.rotation.x = -1.0;
      this.body.rotation.y = 0;
      this.body.scale.set(PLAYER_SCALE * 1.08, PLAYER_SCALE * 0.82, PLAYER_SCALE * 1.12);
    } else if (this.state === "hit") {
      const remaining = Math.max(0, Math.min(1, (this.tumbleUntil - now) / 980));
      const recover = 1 - remaining;
      const wobble = Math.sin(recover * Math.PI * 5) * (1 - recover);
      this.parts.ll.rotation.x = -0.9 + wobble * 0.25;
      this.parts.rl.rotation.x = 1.05 - wobble * 0.2;
      this.parts.ll.rotation.z = -0.2;
      this.parts.rl.rotation.z = 0.2;
      this.parts.ll.position.set(-0.28, 0.58, 0.1);
      this.parts.rl.position.set(0.28, 0.58, 0.1);
      this.body.rotation.x = -1.08 * remaining + wobble * 0.18;
      this.body.rotation.y = 0;
      this.body.rotation.z = this.tumbleSpin * (1.05 * remaining + wobble * 0.35);
      const squash = Math.sin(recover * Math.PI) * 0.22;
      this.body.scale.set(
        PLAYER_SCALE * (1 + squash * 0.7),
        PLAYER_SCALE * (1 - squash),
        PLAYER_SCALE * (1 + squash * 0.45),
      );
      if (this.fallPose) {
        const spin = now * 0.006;
        this.parts.ll.rotation.x = -1.2 + Math.cos(spin * 0.9) * 0.35;
        this.parts.rl.rotation.x = 1.15 + Math.sin(spin * 1.2) * 0.35;
        this.body.rotation.x += dt * 3.2;
        this.body.rotation.z += this.tumbleSpin * dt * 4.4;
        this.body.scale.setScalar(PLAYER_SCALE);
      }
    }

    // Slide drops the body slightly so it reads as "low to the ground" but
    // never below it. Jump height adds on top.
    this.body.position.x = 0;
    this.body.position.y = this.state === "slide" ? -0.2 : 0;
    if (this.state === "run") {
      this.body.position.x = Math.sin(this.runT) * (0.035 - boostPower * 0.015) * moveIntensity;
      this.body.position.y += Math.abs(Math.sin(this.runT * 2)) * (0.11 - boostPower * 0.065) * moveIntensity;
      this.body.position.y += Math.sin(now * 0.0032) * 0.018 * (1 - Math.min(1, moveIntensity));
    }
    this.body.position.y += this.y;

    // Lateral lean — driven by current lane velocity for a natural body bank.
    if (this.state === "run" || this.state === "jump") {
      const lean = Math.max(-0.55, Math.min(0.55, -this.laneVel * 0.07));
      this.body.rotation.z += (lean - this.body.rotation.z) * Math.min(1, dt * 10);
    } else {
      this.body.rotation.z += (0 - this.body.rotation.z) * Math.min(1, dt * 10);
    }

    this.body.visible = true;
  }
}
